import {
  ChevronDownIcon,
  ChevronUpIcon,
  DeleteIcon,
  DownloadIcon,
} from "@chakra-ui/icons";
import {
  Box,
  Button,
  Collapse,
  Flex,
  HStack,
  IconButton,
  Input,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
} from "@chakra-ui/react";
import dayjs from "dayjs";
import React, { useEffect, useState, useCallback } from "react";
import * as XLSX from "xlsx";
import axiosInstance from "../../config/axiosInstance";
import CustomDateRangePickers from "../Customer-dashboard/CustomerDateRangePickers";
import PaginationControls from "../Customer-dashboard/ReceptionistOrders/PaginationControls";

const Customers = () => {
  const [activeFilter, setActiveFilter] = useState("Today");
  const [dateRange, setDateRange] = useState({});
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const toast = useToast();

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteCustomerPhone, setDeleteCustomerPhone] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const filterOptions = ["Today", "Last 7 Days", "Last 30 Days", "Custom Date"];

  const getDates = (option) => {
    const today = dayjs().startOf("day");
    switch (option) {
      case "Today":
        return {
          fromDate: today.toISOString(),
          toDate: today.endOf("day").toISOString(),
        };
      case "Last 7 Days":
        return {
          fromDate: today.subtract(7, "day").toISOString(),
          toDate: today.endOf("day").toISOString(),
        };
      case "Last 30 Days":
        return {
          fromDate: today.subtract(30, "day").toISOString(),
          toDate: today.endOf("day").toISOString(),
        };
      default:
        return {};
    }
  };

  const handleFilterClick = (option) => {
    setActiveFilter(option);
    setDateRange(getDates(option));
    setPage(1);
  };

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/api/admin-stats/customers", {
        params: {
          fromDate: dateRange.fromDate,
          toDate: dateRange.toDate,
          search,
          page,
          limit: pageSize,
        },
      });
      setCustomers(res.data.customers || []);
      setTotalPages(res.data.totalPages || 1);
    } catch (err) {
      toast({ title: "Failed to load customers", status: "error" });
    } finally {
      setLoading(false);
    }
  }, [dateRange, search, page, pageSize, toast]);

  const handleDelete = (phone) => {
    setDeleteCustomerPhone(phone);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteCustomerPhone) return;
    setDeleteLoading(true);
    try {
      await axiosInstance.delete(`/api/admin-stats/customers/${deleteCustomerPhone}`);
      toast({ title: "Customer deleted", status: "success" });
      fetchCustomers();
      setDeleteModalOpen(false);
      setDeleteCustomerPhone(null);
    } catch (err) {
      toast({ title: "Delete failed", status: "error" });
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleExportToExcel = () => {
    if (customers.length === 0) {
      toast({ title: "No data to export", status: "warning" });
      return;
    }

    // Prepare data for Excel
    const exportData = customers.map((cust, index) => ({
      "S.No": (page - 1) * pageSize + index + 1,
      Name: cust.name,
      Email: cust.email,
      Phone: cust.phone,
      "Date Joined": dayjs(cust.createdAt).format("DD/MM/YYYY"),
      Orders: cust.ordersCount,
    }));

    // Create a new workbook
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Customers");

    // Set column widths
    const columnWidths = [
      { wch: 8 },   // S.No
      { wch: 20 },  // Name
      { wch: 25 },  // Email
      { wch: 15 },  // Phone
      { wch: 15 },  // Date Joined
      { wch: 10 },  // Orders
    ];
    ws["!cols"] = columnWidths;

    // Generate filename with date
    const fileName = `customers_${dayjs().format("DD_MM_YYYY_HH_mm_ss")}.xlsx`;

    // Write file
    XLSX.writeFile(wb, fileName);
    toast({ title: "Exported successfully", status: "success" });
  };

  useEffect(() => {
    if (activeFilter !== "Custom Date") {
      const range = getDates(activeFilter);
      setDateRange(range);
    }
  }, [activeFilter]);

  useEffect(() => {
    if (dateRange.fromDate && dateRange.toDate) {
      fetchCustomers();
    }
  }, [dateRange, search, page, pageSize, fetchCustomers]);

  return (
    <Box p={6} bg="gray.50" minH="100vh">
      <Flex justify="space-between" mb={6} align="center" wrap="wrap">
        <Text fontSize="2xl" fontWeight="bold">
          Customers
        </Text>
        <HStack>
          {filterOptions.map((option) => (
            <Button
              key={option}
              size="sm"
              variant={activeFilter === option ? "solid" : "outline"}
              colorScheme="orange"
              onClick={() => handleFilterClick(option)}
            >
              {option}
            </Button>
          ))}
        </HStack>
      </Flex>

      {activeFilter === "Custom Date" && (
        <Box mb={4}>
          <CustomDateRangePickers
            onChange={({ fromDate, toDate }) =>
              setDateRange({
                fromDate: dayjs(fromDate).startOf("day").toISOString(),
                toDate: dayjs(toDate).endOf("day").toISOString(),
              })
            }
          />
        </Box>
      )}

      <Flex justify="space-between" mb={4}>
        <Input
          placeholder="Search by name or email"
          size="sm"
          maxW="300px"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Button
          leftIcon={<DownloadIcon />}
          colorScheme="green"
          size="sm"
          onClick={handleExportToExcel}
          isDisabled={customers.length === 0}
        >
          Export to Excel
        </Button>
      </Flex>

      <Box bg="white" borderRadius="md" shadow="sm" p={4} overflowX="auto">
        {loading ? (
          <Flex justify="center" p={6}>
            <Spinner />
          </Flex>
        ) : (
          <Table size="sm" minW="1000px">
            <Thead>
              <Tr>
                <Th>S.No</Th>
                <Th>Name</Th>
                <Th>Email</Th>
                <Th>Phone</Th>
                <Th>Date Joined</Th>
                <Th>Orders</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {customers.map((cust, index) => (
                <React.Fragment key={cust._id || index}>
                  <Tr>
                    <Td>{(page - 1) * pageSize + index + 1}</Td>
                    <Td
                      cursor="pointer"
                      onClick={() =>
                        setExpandedRow(expandedRow === index ? null : index)
                      }
                    >
                      <HStack>
                        <Text>{cust.name}</Text>
                        {expandedRow === index ? (
                          <ChevronUpIcon />
                        ) : (
                          <ChevronDownIcon />
                        )}
                      </HStack>
                    </Td>
                    <Td>{cust.email}</Td>
                    <Td>{cust.phone}</Td>
                    <Td>{dayjs(cust.createdAt).format("DD/MM/YYYY")}</Td>
                    <Td>{cust.ordersCount}</Td>
                    <Td>
                      <IconButton
                        icon={<DeleteIcon />}
                        size="xs"
                        aria-label="Delete"
                        onClick={() => handleDelete(cust.phone)}
                      />
                    </Td>
                  </Tr>

                  <Tr>
                    <Td colSpan={7} p={0} border="none">
                      <Collapse in={expandedRow === index} animateOpacity>
                        <Box p={4} bg="gray.50">
                          <Text fontSize="sm">
                            Order details or future data can be placed here.
                          </Text>
                        </Box>
                      </Collapse>
                    </Td>
                  </Tr>
                </React.Fragment>
              ))}
            </Tbody>
          </Table>
        )}

        <PaginationControls
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
        />
      </Box>

      <Modal isOpen={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirm Delete</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            Are you sure you want to delete this customer? This action cannot be undone.
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={() => setDeleteModalOpen(false)}
              isDisabled={deleteLoading}
            >
              Cancel
            </Button>
            <Button
              colorScheme="red"
              onClick={confirmDelete}
              isLoading={deleteLoading}
            >
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Customers;
